// Billing › Invoice Workbook › Build invoice folder: one zip laid out the way the
// invoice scripts leave a billing period in Downloads, priced off the same model
// as the Invoice Workbook (invoiceWorkbookModel):
//
//   Billing <M.D>-<M.D.YY> <job>.zip
//     <job> <short name>/Billing_Summary_<job>_<tag>.pdf          the client's billing summary
//     <job> <short name>/Billing_Review_<job>_<tag>_DRAFT.pdf     the internal review (per person, per unit, the checks)
//     <job> <short name>/Invoice_Package_<job>_<tag>.pdf          summary + the signed week summaries (DocuSign copies)
//     <job> <short name>/Labor_Analysis_<job>_<period>_Actual_Wage.pdf
//     <job> <short name>/Invoice_Backup_<job>_<period>.xlsx       the Invoice Workbook itself
//     INTERNAL_Invoice_Preflight.json                             the checks, person by person
//     INTERNAL_Billing_Draft_Totals.json
//
// It refuses to build while a condition the scripts needed a human call for is
// unresolved (invoiceFolderChecks): a week with no reviewed card, a person-week with no
// JDE wage, a person the Attendance & Force Report master does not map, a
// classification the rate sheet lacks, a unit with no rate sheet item, a week summary
// the client has not finished signing. The caller may accept the master exceptions
// (bill on the card's classification, Seth 10/30/26); every acceptance is listed on the
// review and in the preflight file. Phase 1 (Seth 11/2/26): the button and the
// refusals; the per-job exception tables are phase 2.

import moment from 'moment';
import { weekDates, weekLabel, buildInvoiceWorkbook, workbookFileName } from './billingInvoiceWorkbookXlsx';
import { OT_STRAIGHT_CLASS } from './billingPricing';
import { CLASS_ALIAS, normCls, sheetClassOf } from './billingInvoiceExceptions';

export const CLASS_JOB = '54026070'; // the master that carries the billing classifications
/** The masters read for a job: the classification master, the job's own, then 71's (77's crew is 71's crew and 77 keeps no master). */
export const masterJobsFor = (job) => [...new Set([CLASS_JOB, String(job), '54026071'])];
export { CLASS_ALIAS };
/** The VoltaGrid jobs: the job master's AR name is blank on some of them, so the documents fall back to the client's name. */
export const VOLTAGRID_JOBS = ['54026070', '54026071', '54026076', '54026077'];
export const clientNameFor = (job, client) => String(client || '').trim() || (VOLTAGRID_JOBS.includes(String(job)) ? 'VoltaGrid LLC' : '');
const GONE = ['Greg Sattler']; // left VoltaGrid 9/23/26; an old recipient row of his does not hold a week open
const DEAD = ['voided', 'expired', 'failed', 'deleted'];

export const round2 = (n) => { const v = Number(n) || 0; return Math.round((v + Number.EPSILON * v) * 100) / 100; };
export const money = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const h2 = (n) => (Number(n) ? Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '');
// a week's hour totals on the labor analysis, whole hours: "1,418.63" in bold did not fit
// the OT hrs column and printed as an ellipsis on 71's 9/21–10/4 analysis (Seth 11/2)
const h0 = (n) => (Number(n) ? Math.round(Number(n)).toLocaleString('en-US') : '');
const fmtShort = (d) => moment(d, 'YYYY-MM-DD').format('M/D/YY');
const fmtDot = (d) => moment(d, 'YYYY-MM-DD').format('M.D.YY');
const sum = (a, k) => round2(a.reduce((s, x) => s + (Number(typeof k === 'function' ? k(x) : x[k]) || 0), 0));
const pctText = (f) => `${round2((Number(f) || 0) * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;

// ── names ──
export const shortJobName = (jobName) => String(jobName || '').replace(/\s*\(.*$/, '').trim();
export const jobFolderName = (job, jobName) => `${job}${shortJobName(jobName) ? ` ${shortJobName(jobName)}` : ''}`;
/** "10.5-10.18.26", as the scripts tag a period. */
export const periodTag = (model) => `${moment(model.range.from, 'YYYY-MM-DD').format('M.D')}-${fmtDot(model.range.to)}`;
/** "10.5.26-10.18.26", as the analysis and backup files are named. */
export const periodLong = (model) => `${fmtDot(model.range.from)}-${fmtDot(model.range.to)}`;
export const folderFiles = (model, jobName) => {
    const dir = jobFolderName(model.job, jobName);
    const tag = periodTag(model);
    return {
        dir,
        summary: `${dir}/Billing_Summary_${model.job}_${tag}.pdf`,
        review: `${dir}/Billing_Review_${model.job}_${tag}_DRAFT.pdf`,
        pkg: `${dir}/Invoice_Package_${model.job}_${tag}.pdf`,
        analysis: `${dir}/Labor_Analysis_${model.job}_${periodLong(model)}_Actual_Wage.pdf`,
        backup: `${dir}/${workbookFileName(model)}`,
        preflight: 'INTERNAL_Invoice_Preflight.json',
        totals: 'INTERNAL_Billing_Draft_Totals.json',
    };
};
export const zipName = (model) => `Billing ${periodTag(model)} ${model.job}.zip`;

// ── the Attendance & Force Report masters ──
/**
 * The masters as one lookup: rosters in priority order, each { job, res } where
 * res is readAttendanceRoster's answer ({ found, roster: [{ id, name, billCls }] }).
 * A person is on the master when any has them; the billing classification comes
 * from the first that fills it in.
 */
export const masterOf = (rosters) => {
    const people = {};
    const found = [];
    (rosters || []).forEach(({ job, res }) => {
        if (!res || !res.found) return;
        found.push(String(job));
        (res.roster || []).forEach((p) => {
            const id = String(p?.id ?? '').trim().replace(/^0+(?=\d)/, '');
            if (!id) return;
            const cur = people[id] || (people[id] = { id, name: p.name || '', billCls: '', job: String(job) });
            if (!cur.billCls && String(p.billCls || '').trim()) { cur.billCls = String(p.billCls).trim(); cur.clsJob = String(job); }
            if (!cur.name && p.name) cur.name = p.name;
        });
    });
    return { people, found };
};

// ── the week summary envelopes ──
const low = (s) => String(s || '').toLowerCase();
const dead = (e) => DEAD.includes(low(e?.Status));
const mmdd = (d) => moment(d, 'YYYY-MM-DD').format('MMDD');
const weekEndingOf = (d) => { const m = moment(String(d || '').slice(0, 10), 'YYYY-MM-DD'); return m.isValid() ? m.add((7 - m.day()) % 7, 'days').format('YYYY-MM-DD') : ''; };
/** The script-sent summary's document id: j<nn>s<MMDD>to<MMDD>. */
export const scriptSummaryDocId = (job, we) => { const d = weekDates(we); return `j${String(job).slice(-2)}s${mmdd(d[0])}to${mmdd(d[6])}`; };
/**
 * The newest live week summary envelope over a week: the scripts' j<nn>s… id, or an
 * app-sent summary whose name says Week Summary and whose work date is in the week.
 */
export const weekSummaryEnvelope = (envelopes, job, we) => {
    const id = scriptSummaryDocId(job, we);
    const otherJob = (e) => { const m = /(?:^|\D)(\d{8})(?!\d)/.exec(`${e.Document_Name || ''} ${e.Subject || ''}`); return Boolean(m && m[1] !== String(job)); };
    const list = (envelopes || []).filter((e) => !dead(e) && (
        low(e.Doc_ID) === id
        || (/week summary/i.test(`${e.Document_Name || ''} ${e.Subject || ''}`) && weekEndingOf(e.Work_Date) === we && !otherJob(e))
    ));
    return list.sort((a, b) => Number(b.RowID) - Number(a.RowID))[0] || null;
};
/** Where a week's summary stands with the client: { env, complete, signed, signers, outstanding, why }. */
export const weekSignature = (env) => {
    if (!env) return { env: null, complete: false, signed: false, signers: '', outstanding: '', why: 'no week summary envelope on record' };
    const reps = (env.recipients || []).filter((r) => (r.Role === 'All' || !r.Role) && !GONE.includes(r.Name));
    const outstanding = reps.filter((r) => low(r.Status) !== 'completed');
    const completed = low(env.Status) === 'completed';
    return {
        env,
        complete: completed && reps.length > 0 && outstanding.length === 0,
        signed: completed,
        signers: reps.map((r) => r.Name).join(', ') || env.Signer_Name || '',
        outstanding: outstanding.map((r) => r.Name).join(', '),
        why: completed ? (outstanding.length ? `${outstanding.map((r) => r.Name).join(', ')} still to sign` : '') : `envelope "${env.Status}" — no signed PDF yet`,
    };
};
/**
 * The signing page of a week summary packet, from the tabs DocuSign placed
 * (billingSignatureAction 'tabs'): the last page a customer representative signs on.
 * The summary is the leading run of pages through that page. null when no tab was read.
 */
export const signingPageFromTabs = (recipients) => {
    const signers = (recipients?.signers || recipients || []).filter(Boolean);
    const pages = signers.flatMap((s) => (s.tabs?.signHereTabs || []).map((t) => Number(t.pageNumber))).filter((n) => n > 0);
    return pages.length ? Math.max(...pages) : null;
};
/**
 * The same from the PDF's own text when DocuSign kept no tabs on the envelope (71's
 * WE 10/11 Rev 2, sent 10/19): the week summary is the leading run of pages, and its
 * signing page is the one with the customer representative lines, before the first
 * day ticket. pdf.js reads the text in the browser (the worker is public/pdf.worker.min.js).
 * null when no such page is found.
 */
export const signingPageFromText = async (bytes, pageTexts = null) => {
    const texts = pageTexts || await (async () => {
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
        if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
        const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
        const out = [];
        for (let i = 1; i <= doc.numPages; i += 1) {
            const c = await doc.getPage(i).then((pg) => pg.getTextContent()); // eslint-disable-line no-await-in-loop
            out.push(c.items.map((it) => it.str).join(' '));
            // The signing page comes early; no need to read a hundred ticket pages.
            if (i > 1 && /FIELD TICKET DAY SUMMARY|DAILY FIELD TICKET/i.test(out[i - 1])) break;
        }
        return out;
    })();
    if (!/FIELD TICKET WEEK SUMMARY/i.test(texts[0] || '')) return null;
    for (let i = 0; i < texts.length; i += 1) {
        if (i > 0 && /FIELD TICKET DAY SUMMARY|DAILY FIELD TICKET/i.test(texts[i])) return null;
        if (/Customer Representative|VoltaGrid Approver/i.test(texts[i]) && /Signature/i.test(texts[i])) return i + 1;
    }
    return null;
};

// ── the checks ──
/**
 * Everything that must be settled before the folder builds.
 *   model       invoiceWorkbookModel's result
 *   master      masterOf(...)
 *   laborSheet  the job's rate sheet labor rows [{ name, wage }]
 *   weekSigs    { [we]: weekSignature(...) }
 *   invoice     the job's invoice terms { po, poLine, poLineName, afe, terms, mobCredit }
 *   exceptions  parseInvoiceExceptions(...): people accepted off the master by Emp ID; the class
 *               and unit rules are already applied by the model (labor rows carry clsRule,
 *               equipment rows skipped)
 *   options     { acceptMaster: bill master exceptions on the card classification; allowUnsigned: stamp an open week AWAITING SIGNATURE }
 * Returns { blocks, warnings, accepted, settled, rows }: blocks stop the build; accepted are the
 * master exceptions let through by the switch; settled are the ones the exception tables
 * answer; rows is the person-week record for the preflight file.
 */
export const invoiceFolderChecks = ({ model, master, laborSheet, weekSigs = {}, invoice = {}, exceptions = null, options = {} }) => {
    const blocks = [];
    const warnings = [];
    const accepted = [];
    const settled = [];
    const acceptedPeople = {};
    ((exceptions && exceptions.people) || []).forEach((p) => { acceptedPeople[String(p.id).replace(/^0+(?=\d)/, '')] = p; });
    const sheet = {};
    (laborSheet || []).forEach((r) => { if (r && r.name) sheet[normCls(r.name)] = r; });
    const resolve = (cls) => sheetClassOf(cls, laborSheet) || String(cls || '').replace(/&amp;/g, '&').trim();
    const onSheet = (cls) => Boolean(sheetClassOf(cls, laborSheet));
    const targetWage = (cls) => { const r = sheet[normCls(resolve(cls))]; return r ? Number(r.wage) || null : null; };

    (model.weekInfo || []).forEach((w) => {
        if (w.laborBasis !== 'card') blocks.push({ key: 'card', we: w.we, text: `${weekLabel(w.we)}: no reviewed time card on SharePoint (labor would come off the HeavyJob cards). Import Nickie's reviewed workbook for the week.` });
        else if (w.equipBasis === 'hj') blocks.push({ key: 'card', we: w.we, text: `${weekLabel(w.we)}: the reviewed time card's Equipment tab was not read (equipment would come off the HeavyJob cards).` });
    });
    if (!model.jc600) blocks.push({ key: 'wage', text: 'JDE payroll was not read and no JC600 export is loaded — no person can be priced.' });

    const rows = [];
    const seen = new Set();
    (model.labor || []).forEach((l) => {
        const k = `${l.we}|${l.id}`;
        if (seen.has(k)) return;
        seen.add(k);
        const id = String(l.id).replace(/^0+(?=\d)/, '');
        const p = (master?.people || {})[id];
        const masterCls = p?.billCls || '';
        const problems = [];
        const settledBy = [];
        const personOk = acceptedPeople[id];
        if (!p) { if (personOk) settledBy.push(`accepted off the master${personOk.note ? ` (${personOk.note})` : ''}`); else problems.push('not on the Attendance & Force Report master'); }
        else if (!masterCls) { if (personOk) settledBy.push(`accepted with no master classification${personOk.note ? ` (${personOk.note})` : ''}`); else problems.push('no billing classification on the master'); }
        else if (!onSheet(masterCls) && l.clsRule !== 'as') problems.push(`master classification "${masterCls}" is not on the rate sheet`);
        // The master's rate sheet line is what the person bills under; the card's wording is
        // only a note when it differs. A class rule is the call where the master has no line.
        if (l.clsRule === 'master') { if (normCls(l.cardCls) !== normCls(l.cls)) settledBy.push(`billed as the master's "${l.cls}" (the card says "${l.cardCls}")`); }
        else if (l.clsRule === 'as') settledBy.push(`classification "${masterCls && !onSheet(masterCls) ? masterCls : l.cardCls}" billed as "${l.cls}" (classification rule)`);
        else if (l.clsRule === 'keep') settledBy.push(`card classification "${l.cls}" kept as worded (classification rule)`);
        else if (!onSheet(l.cls)) problems.push(`card classification "${l.cls}" is not on the rate sheet`);
        const who = `${weekLabel(l.we)} ${l.name} (${l.id}) ${h2(l.hrs)} hrs`;
        if (l.wage == null && model.jc600) blocks.push({ key: 'wage', we: l.we, id: l.id, text: `${who}: no JDE wage in the JC600 for this job — cannot be priced.` });
        if (settledBy.length) settled.push({ we: l.we, id: l.id, name: l.name, text: `${who} — ${settledBy.join('; ')}` });
        if (problems.length) {
            const text = `${who} — ${problems.join('; ')}`;
            if (options.acceptMaster) accepted.push({ we: l.we, id: l.id, name: l.name, cls: l.cls, masterCls, hrs: l.hrs, problems, text: `${text}; billed on the card classification "${l.cls}"` });
            else blocks.push({ key: 'master', we: l.we, id: l.id, text });
        }
        rows.push({
            we: l.we, id: l.id, name: l.name, cardCls: l.cardCls || l.cls, billedCls: l.cls, clsRule: l.clsRule || '', masterCls, onMaster: Boolean(p), masterJob: p?.job || '', rateClass: onSheet(l.cls) ? resolve(l.cls) : '',
            targetWage: targetWage(l.cls), hrs: l.hrs, otherJobs: l.otherJobs || '', wage: l.wage, wageNote: l.wageNote, problems, settledBy,
        });
    });

    // One block per card row: a row that became N units ("#k of N") is listed once.
    const unitOf = (e) => String(e.unit || '').replace(/ #\d+ of \d+$/, '');
    const unnamed = [...new Set((model.equip || []).filter((e) => !e.item && !e.skipped).map((e) => `${weekLabel(e.we)} ${e.desc}${unitOf(e) ? ` (${unitOf(e)})` : ''}`))];
    unnamed.forEach((u) => blocks.push({ key: 'equip', text: `${u}: no rate sheet item — bill it as a rate sheet item or leave it off in the unit exceptions, or map it in Rates & Rules.` }));
    const skipped = [...new Set((model.equip || []).filter((e) => e.skipped).map((e) => `${weekLabel(e.we)} ${e.desc}${unitOf(e) ? ` (${unitOf(e)})` : ''}`))];
    skipped.forEach((u) => settled.push({ text: `${u} — left off the invoice (unit exception)` }));
    (model.equip || []).filter((e) => e.via === 'exception' && e.item).forEach((e) => settled.push({ text: `${weekLabel(e.we)} ${e.desc}${e.unit ? ` (${e.unit})` : ''} — billed as "${e.item}" (unit exception)` }));

    (model.weeks || []).forEach((we) => {
        const s = weekSigs[we];
        if (!s || !s.complete) {
            const text = `${weekLabel(we)}: the week summary is not signed by every customer representative (${s?.why || 'no envelope'}).`;
            if (options.allowUnsigned && s?.env) warnings.push(`${text} The package stamps it AWAITING SIGNATURE.`);
            else blocks.push({ key: 'signature', we, text });
        } else if (s.complete && !(s.pages > 0)) {
            blocks.push({ key: 'signature', we, text: `${weekLabel(we)}: the signing page of the signed week summary could not be found (no DocuSign tabs read), so the summary pages cannot be cut for the package.` });
        }
    });

    (model.flags || []).filter((f) => !/no reviewed time card|no rate sheet item|no JC600 wage|not priced|Equipment tab was not read/i.test(f)).forEach((f) => warnings.push(f));
    if (!invoice.po) warnings.push('No purchase order is on file for this job (Billing settings → invoice.po): the summary prints "Purchase Order To be confirmed".');
    accepted.forEach((a) => warnings.push(`Master exception accepted: ${a.text}.`));
    if (settled.length) warnings.push(`${settled.length} item${settled.length === 1 ? '' : 's'} settled by the exception tables (listed on the review and in the preflight record).`);
    return { blocks, warnings, accepted, settled, rows };
};

// ── the PDFs (jsPDF + autotable, the invoice scripts' chrome) ──
const INK = [23, 32, 42]; const MUTE = [120, 128, 138]; const BLUE = [21, 82, 150]; const RED = [206, 32, 40]; const LINE = [223, 227, 232]; const FILL = [246, 247, 249];
const M = 40;
const pageChrome = (doc, footerLeft) => {
    const W = doc.internal.pageSize.getWidth(); const H = doc.internal.pageSize.getHeight();
    const pages = doc.getNumberOfPages();
    for (let p = 1; p <= pages; p += 1) {
        doc.setPage(p);
        doc.setDrawColor(...LINE).setLineWidth(0.6); doc.line(M, H - 32, W - M, H - 32);
        doc.setFont('helvetica', 'normal').setFontSize(6.8).setTextColor(...MUTE);
        doc.text(footerLeft, M, H - 22); doc.text(`Page ${p} of ${pages}`, W - M, H - 22, { align: 'right' });
    }
};
const drawHeader = (doc, logos, title, badge, rightText, subLeft, subRight) => {
    const W = doc.internal.pageSize.getWidth(); const usable = W - 2 * M;
    if (logos?.strike) doc.addImage(logos.strike, 'JPEG', M, M - 2, 100, 23);
    if (logos?.client) doc.addImage(logos.client, 'JPEG', W - M - 112, M + 2, 112, 16);
    doc.setFillColor(...BLUE); doc.rect(M, M + 30, usable - 46, 2.2, 'F');
    doc.setFillColor(...RED); doc.rect(W - M - 40, M + 30, 40, 2.2, 'F');
    let y = M + 52;
    doc.setFont('helvetica', 'bold').setFontSize(7.5);
    const bw = doc.getTextWidth(badge) + badge.length + 14;
    doc.setFontSize(9.5);
    const roomForTitle = usable - doc.getTextWidth(rightText) - 20 - 12 - bw;
    let titleSize = 16; doc.setFontSize(titleSize);
    while (titleSize > 11 && doc.getTextWidth(title) + title.length * 1.5 > roomForTitle) { titleSize -= 0.5; doc.setFontSize(titleSize); }
    doc.setTextColor(...INK); doc.text(title, M, y, { charSpace: 1.5 });
    const titleW = doc.getTextWidth(title) + title.length * 1.5;
    doc.setFontSize(7.5);
    const bx = M + titleW + 12;
    doc.setFillColor(...BLUE); doc.roundedRect(bx, y - 11, bw, 15, 7.5, 7.5, 'F');
    doc.setTextColor(255, 255, 255); doc.text(badge, bx + 7, y - 0.5, { charSpace: 1 });
    doc.setFont('helvetica', 'bold').setFontSize(9.5).setTextColor(...INK); doc.text(rightText, W - M, y, { align: 'right' });
    y += 14;
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...MUTE);
    doc.text(subLeft.length > 112 ? `${subLeft.slice(0, 111).trimEnd()}…` : subLeft, M, y);
    if (subRight) doc.text(subRight, W - M, y, { align: 'right' });
    return y + 20;
};
const baseTable = () => ({
    margin: { left: M, right: M, top: M + 8, bottom: M + 30 }, theme: 'plain',
    styles: { font: 'helvetica', fontSize: 7.3, cellPadding: { top: 3.4, bottom: 3.4, left: 3, right: 3 }, textColor: INK, lineColor: LINE, lineWidth: { top: 0.5 }, overflow: 'ellipsize', valign: 'middle' },
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.2, halign: 'center', cellPadding: { top: 4.5, bottom: 4.5, left: 2, right: 2 }, lineWidth: 0 },
    footStyles: { fillColor: FILL, textColor: INK, fontStyle: 'bold', lineColor: INK, lineWidth: { top: 1 } },
    alternateRowStyles: { fillColor: [250, 251, 252] }, showFoot: 'lastPage',
});
const poStrip = (invoice, extra) => {
    const parts = invoice.po
        ? [`Purchase Order ${invoice.po}`, invoice.poLine ? `Line ${invoice.poLine}${invoice.poLineName ? ` — ${invoice.poLineName}` : ''}` : '', invoice.afe ? `AFE ${invoice.afe}` : '', invoice.terms || 'Net 30']
        : [invoice.so || 'Service Order', 'Purchase Order To be confirmed', invoice.terms || 'Net 30'];
    return [...parts.filter(Boolean), ...(extra ? [extra] : [])].join('     ·     ');
};

/** The labor rows grouped by week and classification, hours-weighted rates. */
export const laborByClass = (model, we) => {
    const by = {};
    (model.labor || []).filter((l) => l.we === we).forEach((l) => {
        const c = by[l.cls] || (by[l.cls] = { cls: l.cls, n: 0, hrs: 0, st: 0, ot: 0, stAmt: 0, otAmt: 0, amt: 0 });
        c.n += 1; c.hrs = round2(c.hrs + l.hrs); c.st = round2(c.st + l.st); c.ot = round2(c.ot + l.ot);
        c.stAmt = round2(c.stAmt + l.stAmt); c.otAmt = round2(c.otAmt + l.otAmt); c.amt = round2(c.amt + l.total);
    });
    return Object.values(by).map((c) => ({ ...c, stRate: c.st ? round2(c.stAmt / c.st) : 0, otRate: c.ot ? round2(c.otAmt / c.ot) : 0 })).sort((a, b) => b.amt - a.amt);
};
/** The equipment rows grouped by week and rate sheet item (the client's one-line-per-type layout). */
export const equipByItem = (model, we) => {
    const by = {};
    const order = [];
    (model.equip || []).filter((e) => e.we === we && e.item).forEach((e) => {
        const g = by[e.item] || (by[e.item] = { item: e.item, uom: e.uom, rate: e.rate, fu: e.fu, units: new Set(), qty: 0, amt: 0, fuel: 0, pcts: new Set() });
        if (!order.includes(e.item)) order.push(e.item);
        g.units.add(`${e.desc}|${e.unit}`); g.qty = round2(g.qty + e.qty); g.amt = round2(g.amt + e.amt); g.fuel = round2(g.fuel + e.fuel); g.pcts.add(e.fu ? e.fpct : 0);
    });
    return order.map((k) => { const g = by[k]; const pcts = [...g.pcts]; return { ...g, units: g.units.size, pct: pcts.length === 1 ? pctText(pcts[0]) : pcts.map(pctText).join(' / '), total: round2(g.amt + g.fuel) }; });
};
/** The summary of charges, with the mobilization credit applied when the job carries one. */
export const summaryOfCharges = (model, invoice = {}) => {
    const t = model.totals;
    const mc = invoice.mobCredit && Number(invoice.mobCredit.balance) > 0 ? invoice.mobCredit : null;
    const applied = mc ? round2(Math.min(t.total, Number(mc.balance))) : 0;
    return { labor: t.labor, pdDays: t.pdDays, perDiem: t.pd, equipment: t.equipTotal, total: t.total, mobCredit: mc, applied, due: round2(t.total - applied), left: mc ? round2(Number(mc.balance) - applied) : null };
};

/**
 * The billing summary (clientFacing) or the internal review, as jsPDF bytes.
 *   ctx: { jsPDF, autoTable, logos: { strike, client } (Uint8Array JPEGs or null) }
 */
export const drawBillingSummary = (ctx, { model, jobName, client, invoice = {}, clientFacing, checks, weekSigs = {}, preparedBy }) => {
    const { jsPDF, autoTable, logos } = ctx;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth(); const H = doc.internal.pageSize.getHeight(); const usable = W - 2 * M;
    const base = baseTable();
    const small = { ...base.styles, fontSize: 6.5, cellPadding: { top: 2.6, bottom: 2.6, left: 2, right: 2 } };
    const R = { halign: 'right' };
    const rightHead = (from) => (h) => { if (h.section !== 'body' && h.column.index >= from) h.cell.styles.halign = 'right'; };
    const wrapHead = (fn) => (h) => { fn(h); if (h.section === 'head') h.cell.styles.overflow = 'linebreak'; };
    const { from, to } = model.range;
    const soc = summaryOfCharges(model, invoice);
    let y = drawHeader(doc, logos, clientFacing ? 'BILLING SUMMARY' : 'BILLING REVIEW', clientFacing ? 'LABOR & EQUIPMENT' : 'INTERNAL DRAFT',
        `Work ${fmtShort(from)} – ${fmtShort(to)}`, `Job ${model.job}   —   ${client}   —   ${jobName}`, `Prepared ${moment().format('M/D/YY h:mm A')}${preparedBy ? ` by ${preparedBy}` : ''}`);
    if (clientFacing) {
        doc.setFillColor(...FILL); doc.roundedRect(M, y - 10, usable, 17, 3, 3, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(7.5).setTextColor(...INK); doc.text(poStrip(invoice), M + 10, y + 2);
        y += 28;
    }
    const section = (label, right) => {
        if (y > H - 130) { doc.addPage(); y = M + 8; }
        doc.setFillColor(...BLUE); doc.rect(M, y - 7, 3, 9, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...INK); doc.text(label, M + 9, y, { charSpace: 1.2 });
        if (right) { doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE); doc.text(right, W - M, y, { align: 'right' }); }
        y += 10;
    };
    const table = (opts) => { autoTable(doc, { ...base, startY: y, ...opts }); y = doc.lastAutoTable.finalY + 14; };
    // No explanation text on a client page: the credit tables and figures stand alone (Seth 10/13, 11/2).
    const note = (lines) => {
        if (clientFacing || !lines.length) return;
        doc.setFont('helvetica', 'normal').setFontSize(6.8).setTextColor(...MUTE);
        const wrapped = doc.splitTextToSize(lines.map((l) => `•  ${l}`).join('\n'), usable);
        if (y + wrapped.length * 8.5 > H - 50) { doc.addPage(); y = M + 8; }
        doc.text(wrapped, M, y); y += wrapped.length * 8.5 + 10;
    };
    const weeksText = model.weeks.map((we) => weekLabel(we).replace('WE ', '')).join(' and ');

    // 1. Summary of charges
    section('SUMMARY OF CHARGES', `${money(soc.total)} ${soc.mobCredit ? 'against the mobilization credit' : 'to invoice'}`);
    const body = [
        ['Labor', `Signed field ticket week summaries — actual wage × ${model.cfg.m}`, money(soc.labor)],
        ['Per Diem', `${soc.pdDays} days × $${model.rules.pdRate}`, money(soc.perDiem)],
        ['Equipment + fuel', 'Week summary equipment × rate sheet plus fuel surcharge', money(soc.equipment)],
    ];
    if (soc.mobCredit) body.push(['Less: mobilization credit applied', `Applied against the ${soc.mobCredit.basis || 'remaining mobilization invoice credit balance'} of ${money(soc.mobCredit.balance)}`, `(${money(soc.applied)})`]);
    table({ head: [['Area', 'Basis', 'Amount']], body, foot: [[soc.mobCredit ? 'TOTAL AMOUNT DUE' : 'TOTAL AMOUNT TO BILL', '', money(soc.due)]],
        columnStyles: { 0: { cellWidth: 120 }, 2: { ...R, cellWidth: 110, fontStyle: 'bold' } }, didParseCell: rightHead(2) });
    note(clientFacing ? [
        `Labor and per diem are billed from the field ticket week summaries for the weeks ending ${weeksText}. Equipment is billed from the same summaries at the rate schedule.`,
        'This invoice covers labor and equipment. Third-party services and materials are invoiced separately at cost plus the 12% administrative and handling fee.',
    ] : [
        `Signatures — ${model.weeks.map((we) => { const s = weekSigs[we]; return `${weekLabel(we)}: ${!s?.env ? 'no week summary envelope' : s.complete ? `signed by ${s.signers}` : s.signed ? `${s.outstanding} pending` : s.why}`; }).join('.  ')}.`,
        'FOR REVIEW: section 1 is labor by classification and by person at each person’s JDE wage; section 2 the per diem; section 3 the equipment mapping by unit; section 4 the checks this build passed and what it accepted.',
    ]);

    // 2. Labor by classification per week
    section('1. LABOR', `${h2(model.totals.hrs)} hrs — ${money(model.totals.labor)}`);
    model.weeks.forEach((we) => {
        const rows = model.labor.filter((l) => l.we === we);
        const list = laborByClass(model, we);
        table({ styles: small, head: [[`${weekLabel(we)} — classification`, 'People', 'Hrs', 'ST hrs', 'ST rate', 'OT hrs', 'OT rate', 'Amount']],
            body: list.map((c) => [c.cls, c.n, h2(c.hrs), h2(c.st), c.st ? money(c.stRate) : '', h2(c.ot) || '', c.ot ? money(c.otRate) : '', money(c.amt)]),
            foot: [['Total', rows.length, h2(sum(rows, 'hrs')), h2(sum(rows, 'st')), '', h2(sum(rows, 'ot')), '', money(sum(rows, 'total'))]],
            columnStyles: { 0: { cellWidth: 150 }, 1: { halign: 'center', cellWidth: 40 }, 2: { ...R, cellWidth: 50 }, 3: { ...R, cellWidth: 50 }, 4: { ...R, cellWidth: 52 }, 5: { ...R, cellWidth: 50 }, 6: { ...R, cellWidth: 52 }, 7: { halign: 'right', fontStyle: 'bold', cellWidth: 88 } }, didParseCell: rightHead(2) });
    });
    note([
        `Bill rate = the employee's actual wage × ${model.cfg.m}; overtime × ${model.cfg.otf} for hours over ${model.rules.otAfter} in the week, per person. Rates on a classification line are hours-weighted averages. Hours are the signed field ticket week summaries.`,
        ...(model.labor.some((l) => l.cls === OT_STRAIGHT_CLASS && l.ot) ? ['Project Directors bill overtime hours at their straight rate (Service Order Attachment A carries one Project Director rate for straight time and overtime).'] : []),
        ...(model.otherSource ? [`Where a person worked more than one job in the same week, the first ${model.rules.otAfter} hours of the week are straight time and the rest overtime, in date order, whichever job they were on${model.otherSource === 'payroll' ? ' (the other jobs\u2019 hours from JDE payroll)' : ''}.`] : []),
        ...(clientFacing ? [] : [`Actual wage = the base hourly rate JDE payroll paid the person on this job that week (${model.jc600?.name || 'JC600'}); the rate sheet's target wage is on the labor analysis for comparison only. ${model.labor.filter((l) => l.wage != null && !l.wageOwn).length ? `Wage carried from another week (no payroll on this job that week): ${model.labor.filter((l) => l.wage != null && !l.wageOwn).map((l) => `${l.name} (${weekLabel(l.we)}: ${l.wageNote})`).join('; ')}.` : 'Every person has payroll on this job in the week billed.'}`]),
    ]);
    if (!clientFacing) {
        model.weeks.forEach((we) => {
            const rows = model.labor.filter((l) => l.we === we).sort((a, b) => a.cls.localeCompare(b.cls) || a.name.localeCompare(b.name));
            section(`${weekLabel(we)} — BY PERSON`, `${rows.length} people — ${money(sum(rows, 'total'))}`);
            table({ styles: small, head: [['Employee', 'ID', 'Classification', 'Hrs', 'Other jobs', 'JDE wage', 'ST hrs', 'ST rate', 'OT hrs', 'OT rate', 'Amount', 'Wage source']],
                body: rows.map((l) => [l.name, l.id, l.cls, h2(l.hrs), l.otherJobs || '', l.wage == null ? '—' : money(l.wage), h2(l.st), l.stRate == null ? '' : money(l.stRate), h2(l.ot) || '', l.ot && l.otRate != null ? money(l.otRate) : '', money(l.total), l.wageNote || '']),
                foot: [['Total', '', '', h2(sum(rows, 'hrs')), '', '', h2(sum(rows, 'st')), '', h2(sum(rows, 'ot')), '', money(sum(rows, 'total')), '']],
                columnStyles: { 0: { cellWidth: 86 }, 1: { cellWidth: 32 }, 2: { cellWidth: 84 }, 3: { ...R, cellWidth: 28 }, 4: { cellWidth: 60 }, 5: { ...R, cellWidth: 40 }, 6: { ...R, cellWidth: 28 }, 7: { ...R, cellWidth: 38 }, 8: { ...R, cellWidth: 28 }, 9: { ...R, cellWidth: 38 }, 10: { halign: 'right', fontStyle: 'bold', cellWidth: 50 } }, didParseCell: rightHead(3) });
        });
    }

    // 3. Per diem
    section(clientFacing ? 'PER DIEM — CALCULATION' : '2. PER DIEM', money(model.totals.pd));
    const pdBody = [];
    model.weeks.forEach((we) => {
        const rows = model.perDiem.filter((p) => p.we === we && p.days > 0);
        const dist = {}; rows.forEach((p) => { dist[p.days] = (dist[p.days] || 0) + 1; });
        Object.keys(dist).map(Number).sort((a, b) => b - a).forEach((d) => pdBody.push([weekLabel(we), `${dist[d]} ${dist[d] === 1 ? 'person' : 'people'} × ${d} ${d === 1 ? 'day' : 'days'}`, dist[d] * d, money(model.rules.pdRate), money(round2(dist[d] * d * model.rules.pdRate))]));
        pdBody.push([{ content: `${weekLabel(we)} subtotal`, styles: { fontStyle: 'bold' } }, `${rows.length} people`, { content: String(sum(rows, 'days')), styles: { fontStyle: 'bold' } }, '', { content: money(sum(rows, 'amt')), styles: { fontStyle: 'bold' } }]);
    });
    table({ styles: small, head: [['Week', 'People × per diem days', 'Days', 'Rate', 'Amount']], body: pdBody, foot: [['Total', `${model.perDiem.filter((p) => p.days > 0).length} person-weeks`, String(model.totals.pdDays), '', money(model.totals.pd)]],
        columnStyles: { 0: { cellWidth: 100 }, 2: { ...R, cellWidth: 50 }, 3: { ...R, cellWidth: 60 }, 4: { ...R, cellWidth: 80 } }, didParseCell: rightHead(2) });
    note([`Per diem days = days a person carries per diem on the signed field ticket week summary, billed at the rate schedule's $${model.rules.pdRate}/day. Personnel on a 5+ consecutive-workday assignment get per diem for the 6th and 7th days regardless of hours worked.`,
        ...(clientFacing ? [] : [`JDE paid per diem of ${money(sum(model.perDiem, 'jdePaid'))} for these weeks; ${model.perDiem.filter((p) => Math.abs(p.amt - p.jdePaid) > 0.005).length} person-weeks differ from the card (the card bills).`])]);

    // 4. Equipment
    doc.addPage(); y = M + 8;
    const unitWeeks = new Set(model.equip.map((e) => `${e.we}|${e.desc}|${e.unit}`)).size;
    section(clientFacing ? '2. EQUIPMENT — WEEK SUMMARY × RATE SHEET' : '3. EQUIPMENT — WEEK SUMMARY × RATE SHEET', `${unitWeeks} unit-weeks — ${money(model.totals.equipTotal)}`);
    model.weeks.forEach((we) => {
        const rows = model.equip.filter((e) => e.we === we);
        if (!rows.length) return;
        if (clientFacing) {
            const groups = equipByItem(model, we);
            table({ styles: small, head: [[`${weekLabel(we)} — equipment type (rate sheet item)`, 'Fuel', 'Units', 'Qty', 'Rate', 'Amount', 'Fuel surcharge rate', 'Fuel surcharge amount', 'Line total']],
                body: groups.map((g) => [g.item, g.fu ? 'Yes' : 'No', g.units, `${h2(g.qty)} ${g.uom}`, `${money(g.rate)}/${g.uom}`, money(g.amt), g.pct, money(g.fuel), money(g.total)]),
                foot: [[`${weekLabel(we)} subtotal`, '', String(sum(groups, 'units')), '', '', money(sum(groups, 'amt')), '', money(sum(groups, 'fuel')), money(sum(groups, 'total'))]],
                columnStyles: { 1: { halign: 'center', cellWidth: 30 }, 2: { halign: 'center', cellWidth: 30 }, 3: { ...R, cellWidth: 50 }, 4: { ...R, cellWidth: 60 }, 5: { ...R, cellWidth: 58 }, 6: { ...R, cellWidth: 44 }, 7: { ...R, cellWidth: 58 }, 8: { halign: 'right', fontStyle: 'bold', cellWidth: 62 } }, didParseCell: wrapHead(rightHead(3)) });
            return;
        }
        table({ styles: small, head: [[`${weekLabel(we)} — card equipment`, 'Unit No', 'Rate sheet item', 'Fuel', 'Days', 'Qty', 'Rate', 'Amount', 'Fuel surcharge', 'Line total']],
            body: rows.map((e) => [e.desc, e.unit, e.item || '(none)', e.fu ? 'Yes' : 'No', e.n, `${h2(e.qty)} ${e.uom}`, e.rate == null ? '' : `${money(e.rate)}/${e.uom}`, money(e.amt), `${pctText(e.fu ? e.fpct : 0)} ${money(e.fuel)}`, money(e.total)]),
            foot: [[`${weekLabel(we)} subtotal`, '', '', '', '', '', '', money(sum(rows, 'amt')), money(sum(rows, 'fuel')), money(sum(rows, 'total'))]],
            columnStyles: { 0: { cellWidth: 110 }, 1: { cellWidth: 50 }, 2: { cellWidth: 104 }, 3: { halign: 'center', cellWidth: 24 }, 4: { halign: 'center', cellWidth: 24 }, 5: { ...R, cellWidth: 40 }, 6: { ...R, cellWidth: 48 }, 7: { ...R, cellWidth: 46 }, 8: { ...R, cellWidth: 56 }, 9: { halign: 'right', fontStyle: 'bold', cellWidth: 50 } }, didParseCell: wrapHead(rightHead(4)) });
    });
    const fuelBase = sum(model.equip.filter((e) => e.fu), 'amt');
    const noFuel = round2(model.totals.equip - fuelBase);
    table({ styles: small, head: [['Equipment total', '', '']],
        body: [['Equipment (time card units)', '', money(model.totals.equip)],
            ...(noFuel ? [['Less: units that burn no fuel', '', `(${money(noFuel)})`]] : []), ['Fuel surcharge base (fuel burning equipment)', '', money(fuelBase)],
            ...model.fuelQ.map((f) => [`Fuel surcharge ${pctText(f.pct)}`, `${f.q} Gulf Coast diesel avg $${f.price} (work in ${f.applies})`, money(sum(model.equip.filter((e) => e.fq === f.q), 'fuel'))])],
        foot: [['EQUIPMENT TOTAL', '', money(model.totals.equipTotal)]],
        columnStyles: { 0: { cellWidth: 200 }, 1: { cellWidth: 240 }, 2: { halign: 'right', fontStyle: 'bold', cellWidth: 90 } }, didParseCell: (h) => { if (h.section !== 'body' && h.column.index === 2) h.cell.styles.halign = 'right'; } });
    note([
        'Hours are the week summary’s equipment time card. Hourly units bill the hours recorded (five-hour daily minimum). Daily units bill one daily rate per shift on site that day: the rate schedule quotes a daily rate against a single ten-hour shift, and a day shift and a night shift each draw one.',
        'Rented units on the rate sheet bill at the rate sheet, not the rental invoice (equipment provisions). Temporary and welfare facilities and site communications are part of Contractor’s rates and are not invoiced as separate items.',
        'The fuel surcharge recovers diesel cost, so it runs only on equipment that burns fuel; towed trailers, tool vans and the gas monitors are held out of the base.',
    ]);

    // 5. The checks (review only)
    if (!clientFacing && checks) {
        doc.addPage(); y = M + 8;
        section('4. CHECKS', `${checks.blocks.length} blocking · ${checks.warnings.length} to look at · ${checks.accepted.length} accepted`);
        note([
            ...checks.blocks.map((b) => `BLOCK — ${b.text}`),
            ...checks.warnings.map((w) => `CHECK — ${w}`),
            ...(checks.settled || []).map((x) => `SETTLED — ${x.text}`),
            ...(checks.blocks.length || checks.warnings.length ? [] : ['Every check passed: reviewed cards for every week, every person on the master with a rate sheet classification and a JDE wage, every unit on the rate sheet, every week summary signed.']),
        ]);
    }
    pageChrome(doc, clientFacing
        ? `Strike — Billing Summary — Job ${model.job} — ${client} — work ${fmtShort(from)}–${fmtShort(to)}`
        : `Strike — Billing Review (INTERNAL DRAFT) — Job ${model.job} — work ${fmtShort(from)}–${fmtShort(to)}`);
    return new Uint8Array(doc.output('arraybuffer'));
};

/** The labor analysis: labor by employee per week at the JDE wage, the rate sheet's target wage beside it for reference. */
export const drawLaborAnalysis = (ctx, { model, jobName, client, invoice = {}, rows }) => {
    const { jsPDF, autoTable, logos } = ctx;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth(); const H = doc.internal.pageSize.getHeight(); const usable = W - 2 * M;
    const base = baseTable();
    const small = { ...base.styles, fontSize: 6.8, cellPadding: { top: 2.8, bottom: 2.8, left: 2, right: 2 } };
    const R = { halign: 'right' };
    const rightHead = (n) => (h) => { if (h.section !== 'body' && h.column.index >= n) h.cell.styles.halign = 'right'; };
    const rate3 = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
    const { from, to } = model.range;
    const target = {};
    (rows || []).forEach((r) => { target[`${r.we}|${r.id}`] = r.targetWage; });
    let y = drawHeader(doc, logos, 'LABOR ANALYSIS', 'ACTUAL WAGE', 'Labor & Equipment Invoice',
        `Job ${model.job}   —   ${client}   —   ${jobName}   —   Work ${fmtShort(from)} – ${fmtShort(to)}`, null);
    doc.setFillColor(...FILL); doc.roundedRect(M, y - 10, usable, 17, 3, 3, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(7.5).setTextColor(...INK);
    doc.text(poStrip({ ...invoice, terms: 'Labor at actual wage (JDE)' }), M + 10, y + 2);
    y += 28;
    const section = (text, right) => {
        if (y > H - 130) { doc.addPage(); y = M + 8; }
        doc.setFillColor(...BLUE); doc.rect(M, y - 7, 3, 9, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...INK); doc.text(text, M + 9, y, { charSpace: 1.2 });
        if (right) { doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...MUTE); doc.text(right, W - M, y, { align: 'right' }); }
        y += 10;
    };
    const table = (opts) => { autoTable(doc, { ...base, startY: y, ...opts }); y = doc.lastAutoTable.finalY + 14; };
    section('LABOR — BY EMPLOYEE', `${h2(model.totals.hrs)} hrs — ${money(model.totals.labor)} invoiced`);
    model.weeks.forEach((we) => {
        const wr = model.labor.filter((l) => l.we === we).sort((a, b) => a.cls.localeCompare(b.cls) || a.name.localeCompare(b.name));
        table({ styles: small, head: [[`${weekLabel(we)} — employee`, 'ID', 'Classification', 'ST hrs', 'OT hrs', 'Total hrs', 'Payroll wage', 'Target payroll wage', 'Invoiced ST rate', 'Target ST rate', 'Invoiced OT rate', 'Invoiced']],
            body: wr.map((r) => { const t = target[`${we}|${r.id}`]; return [r.name, r.id, r.cls, h2(r.st), h2(r.ot), h2(r.hrs), r.wage == null ? '—' : rate3(r.wage), t ? money(t) : '', r.stRate == null ? '' : money(r.stRate), t ? money(round2(t * model.cfg.m)) : '', r.ot && r.otRate != null ? money(r.otRate) : '', money(r.total)]; }),
            foot: [[{ content: `${weekLabel(we)} total — ${wr.length} people`, colSpan: 3 }, h0(sum(wr, 'st')), h0(sum(wr, 'ot')), h0(sum(wr, 'hrs')), '', '', '', '', '', money(sum(wr, 'total'))]],
            headStyles: { ...base.headStyles, overflow: 'linebreak' },
            columnStyles: { 0: { cellWidth: 76, overflow: 'linebreak' }, 1: { cellWidth: 28 }, 2: { cellWidth: 82, overflow: 'linebreak' }, 3: { ...R, cellWidth: 32 }, 4: { ...R, cellWidth: 30 }, 5: { ...R, cellWidth: 32 },
                6: { ...R, cellWidth: 38 }, 7: { ...R, cellWidth: 42 }, 8: { ...R, cellWidth: 42 }, 9: { ...R, cellWidth: 42 }, 10: { ...R, cellWidth: 42 }, 11: { halign: 'right', fontStyle: 'bold', cellWidth: 46 } },
            didParseCell: rightHead(3) });
    });
    pageChrome(doc, `Strike — Labor Analysis — Labor & Equipment Invoice — Job ${model.job} — ${client} — work ${fmtShort(from)}–${fmtShort(to)}`);
    return new Uint8Array(doc.output('arraybuffer'));
};

/**
 * The client package: a contents page, the billing summary, then each week's signed
 * summary pages (the leading pages through the signing page), every page stamped.
 *   weeks: [{ we, bytes, pages (the signing page number), complete, signed, signers, outstanding }]
 */
export const assembleInvoicePackage = async (ctx, { model, jobName, client, invoice = {}, summaryBytes, weeks }) => {
    const { jsPDF, autoTable, PDFLib } = ctx;
    const { from, to } = model.range;
    const summaryDoc = await PDFLib.PDFDocument.load(summaryBytes);
    const soc = summaryOfCharges(model, invoice);
    for (const w of weeks) {
        w.doc = await PDFLib.PDFDocument.load(w.bytes, { ignoreEncryption: true }); // eslint-disable-line no-await-in-loop
        const n = w.doc.getPageCount();
        const keep = Math.min(n, Math.max(1, Number(w.pages) || n));
        w.keep = Array.from({ length: keep }, (_, k) => k);
    }
    let at = 2;
    const parts = [{ label: 'A', title: 'Billing summary - summary of charges, labor, per diem and equipment', pages: summaryDoc.getPageCount() }];
    parts[0].start = at; at += parts[0].pages;
    weeks.forEach((w, i) => {
        const p = { label: String.fromCharCode(66 + i), title: `Signed field ticket week summary - Week ending ${fmtShort(w.we)}${w.complete ? '' : w.signed ? ` (${w.outstanding} pending)` : ' (AWAITING SIGNATURE)'}`, pages: w.keep.length, start: at, week: w };
        at += p.pages;
        parts.push(p);
    });
    const totalPages = at - 1;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold').setFontSize(17).setTextColor('#0f172a');
    doc.text('INVOICE PACKAGE', M, M + 14);
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor('#475569');
    doc.text(`Job ${model.job}   —   ${client}   —   ${jobName}`, M, M + 32);
    doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor('#0f172a');
    doc.text(`Work ${fmtShort(from)} – ${fmtShort(to)}   ·   Labor & Equipment`, M, M + 50);
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor('#64748b');
    doc.text(`${totalPages} pages   ·   prepared ${moment().format('M/D/YY h:mm A')}`, M, M + 64);
    doc.setDrawColor('#e2e8f0').setLineWidth(0.7).line(M, M + 76, W - M, M + 76);
    autoTable(doc, {
        startY: M + 92, margin: { left: M, right: M },
        head: [['', 'Section', 'Pages', 'Starts']],
        body: parts.map((p) => [p.label, p.title, String(p.pages), `p. ${p.start}`]),
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 7, textColor: '#0f172a', lineColor: '#e2e8f0', lineWidth: 0.5, overflow: 'linebreak' },
        headStyles: { fillColor: '#f1f5f9', textColor: '#475569', fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { cellWidth: 26, fontStyle: 'bold', halign: 'center' }, 2: { cellWidth: 46, halign: 'center' }, 3: { cellWidth: 52, halign: 'center', fontStyle: 'bold' } },
        didParseCell: (d) => { if (d.section === 'body' && d.column.index === 1 && /AWAITING|pending\)/.test(String(d.cell.raw))) d.cell.styles.textColor = '#b45309'; },
    });
    // No basis-of-charges text on the cover: the summary behind it carries the figures (Seth 11/2).

    const out = await PDFLib.PDFDocument.load(doc.output('arraybuffer'));
    const font = await out.embedFont(PDFLib.StandardFonts.Helvetica);
    const bold = await out.embedFont(PDFLib.StandardFonts.HelveticaBold);
    const stamp = (pg, text, warn) => {
        const { width, height } = pg.getSize();
        const f = warn ? bold : font;
        const w = f.widthOfTextAtSize(text, 7) + 10;
        pg.drawRectangle({ x: width - w - 12, y: height - 20, width: w, height: 13, color: warn ? PDFLib.rgb(1, 0.97, 0.88) : PDFLib.rgb(1, 1, 1), borderColor: warn ? PDFLib.rgb(0.71, 0.33, 0.04) : PDFLib.rgb(0.47, 0.5, 0.54), borderWidth: 0.5 });
        pg.drawText(text, { x: width - w - 7, y: height - 16, size: 7, font: f, color: warn ? PDFLib.rgb(0.71, 0.33, 0.04) : PDFLib.rgb(0.09, 0.13, 0.16) });
    };
    const sumPages = await out.copyPages(summaryDoc, summaryDoc.getPageIndices());
    sumPages.forEach((pg, i) => { out.addPage(pg); stamp(pg, `A · page ${parts[0].start + i} of ${totalPages}`); });
    for (const p of parts.slice(1)) {
        const w = p.week;
        const pages = await out.copyPages(w.doc, w.keep); // eslint-disable-line no-await-in-loop
        pages.forEach((pg, i) => {
            out.addPage(pg);
            stamp(pg, w.complete ? `${p.label} · page ${p.start + i} of ${totalPages}` : `${p.label} · ${w.signed ? `${String(w.outstanding).toUpperCase()} SIGNATURE PENDING` : 'AWAITING SIGNATURE'} · page ${p.start + i} of ${totalPages}`, !w.complete);
        });
    }
    if (out.getPageCount() !== totalPages) throw new Error(`assembled ${out.getPageCount()} pages, expected ${totalPages}`);
    return { bytes: await out.save(), parts, totalPages };
};

/** The preflight record written beside the folder. */
export const preflightRecord = ({ model, checks, master, weekSigs, options, exceptions }) => ({
    exceptions: exceptions ? { terms: exceptions.terms, classes: exceptions.classes, people: exceptions.people, units: exceptions.units } : null,
    asof: new Date().toISOString(), job: model.job, weeks: model.weeks, period: model.range, options,
    wages: model.jc600 ? { source: model.jc600.name, lines: model.jc600.totals?.lines, latest: model.jc600.totals?.latest } : null,
    masters: master?.found || [],
    blocks: checks.blocks, warnings: checks.warnings, accepted: checks.accepted, settled: checks.settled || [], rows: checks.rows,
    otherHours: model.otherSource || '',
    weekSummaries: Object.fromEntries(Object.entries(weekSigs || {}).map(([we, s]) => [we, { rowId: s.env?.RowID, docId: s.env?.Doc_ID, status: s.env?.Status, complete: s.complete, signers: s.signers, outstanding: s.outstanding, signingPage: s.pages, signingPageFrom: s.pagesFrom || '' }])),
});
export const totalsRecord = ({ model, invoice, jobName, client }) => ({
    prepared: model.preparedAt, job: model.job, jobName, client, period: model.range, weeks: model.weeks, invoice,
    summary: summaryOfCharges(model, invoice), totals: model.totals, byWeek: model.byWeek, byClass: model.byClass, byItem: model.byItem, fuel: model.fuelQ,
});

/**
 * Build the whole folder as a zip. Everything the page gathered comes in; the PDF
 * libraries are loaded here so the pure parts above stay import-light.
 *   signedWeeks: [{ we, bytes, pages, complete, signed, signers, outstanding }]
 * Returns { name, bytes, blob, files: [zip paths] }.
 */
export const buildInvoiceFolder = async ({ model, jobName, client: clientIn, invoice = {}, checks, master, weekSigs, options, exceptions, cardFiles, signedWeeks, preparedBy }) => {
    const client = clientNameFor(model.job, clientIn);
    const [{ jsPDF }, atMod, PDFLib, JSZip] = await Promise.all([import('jspdf'), import('jspdf-autotable'), import('pdf-lib'), import('jszip').then((m) => m.default)]);
    const autoTable = atMod.default || atMod.autoTable;
    const logoBytes = async (file) => {
        try {
            const r = await fetch(`${process.env.PUBLIC_URL || ''}/templates/${file}`);
            return r.ok ? new Uint8Array(await r.arrayBuffer()) : null;
        } catch (e) { return null; }
    };
    const [strike, cl] = await Promise.all([logoBytes('strike-logo-flat.jpeg'), logoBytes('voltagrid-logo.jpeg')]);
    const ctx = { jsPDF, autoTable, PDFLib, logos: { strike, client: cl } };
    const files = folderFiles(model, jobName);
    const summary = drawBillingSummary(ctx, { model, jobName, client, invoice, clientFacing: true, weekSigs, preparedBy });
    const review = drawBillingSummary(ctx, { model, jobName, client, invoice, clientFacing: false, checks, weekSigs, preparedBy });
    const analysis = drawLaborAnalysis(ctx, { model, jobName, client, invoice, rows: checks.rows });
    const pkg = await assembleInvoicePackage(ctx, { model, jobName, client, invoice, summaryBytes: summary, weeks: signedWeeks });
    const wb = await buildInvoiceWorkbook(model, { cardFiles });
    const backup = await wb.xlsx.writeBuffer();
    const zip = new JSZip();
    zip.file(files.summary, summary);
    zip.file(files.review, review);
    zip.file(files.pkg, pkg.bytes);
    zip.file(files.analysis, analysis);
    zip.file(files.backup, backup);
    zip.file(files.preflight, JSON.stringify(preflightRecord({ model, checks, master, weekSigs, options, exceptions }), null, 1));
    zip.file(files.totals, JSON.stringify(totalsRecord({ model, invoice, jobName, client }), null, 1));
    const bytes = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
    const blob = new Blob([bytes], { type: 'application/zip' });
    return { name: zipName(model), bytes, blob, files: Object.values(files).filter((f) => f !== files.dir), packagePages: pkg.totalPages, notes: wb.embedNotes, cardTabs: wb.cardTabs };
};

/** Hand a built zip to the browser. */
export const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
};
